import { useEffect, useMemo, useState } from "react"

import { Input, InputGroup, InputGroupText, Select, List, ListItem } from "twyne-new.ui"

import { useCraftProp } from "../utils"
import { UNIT_OPTIONS } from "../utils/predefined_sizes"

export default function ShadowProperty({ prefix, sizeOptions }) {
  const [unit, setUnit] = useState()
  const { value, setValue } = useCraftProp(`${prefix}.value`)
  const isArbitrary = useMemo(() => value && typeof value !== 'string', [value])

  useEffect(() => {
    if (unit === undefined) {
      setUnit(!isArbitrary ? 'px' : value?.x?.split('_')[1] || 'px')
    }
  }, [unit, isArbitrary, value])

  useConvertOldValue({ prefix, setUnit })

  const handleChangeUnit = newUnit => {
    let { x = '', y = '', blur = '' } = value
    x = [x.split('_')[0], newUnit].join('_')
    y = [y.split('_')[0], newUnit].join('_')
    blur = [blur.split('_')[0], newUnit].join('_')
    setValue({ x, y, blur })
    setUnit(newUnit)
  }

  const handleChangeValue = newValue => {
    if (newValue === 'arbitrary' && !isArbitrary) {
      return setValue({})
    }

    setValue(newValue)
  }

  return <>
    <List className='pt-0'>
      <div className='flex flex-col gap-2'>
        <ListItem>
          <div className='grid grid-cols-5 gap-2 items-center w-full'>
            <div className='col-span-1'>Size</div>
            <div className='col-span-4 flex flex-col gap-1 items-center'>
              <div className='w-full flex gap-1 items-center'>
                <Select
                  options={[{ label: 'Arbitrary', value: 'arbitrary' }, ...sizeOptions]}
                  value={isArbitrary ? 'arbitrary' : value}
                  onChange={option => handleChangeValue(option?.value)}
                  variant='accent'
                  clearable
                />
                {isArbitrary && <Select
                  options={UNIT_OPTIONS}
                  value={unit}
                  onChange={option => handleChangeUnit(option?.value)}
                  placeholder='Unit'
                  variant='accent'
                />}
              </div>
              {isArbitrary && <ArbitraryInputs prefix={prefix} unit={unit} />}
            </div>
          </div>
        </ListItem>
      </div>
    </List>
  </>
}

const ArbitraryInputs = ({ prefix, unit }) => {
  const { value, setValue } = useCraftProp(`${prefix}.value`)
  const { x, y, blur } = value

  const getValue = (value = '') => value.split('_')[0]

  const handleChange = (key, newValue) => {
    setValue({ ...value, [key]: newValue ? [newValue, unit].join('_') : '' })
  }

  return <div className='w-full flex flex-col gap-1'>
    <div className='flex gap-1 items-center'>
      <InputGroup className='w-full'>
        <InputGroupText>
          <div className='w-[23px] text-center'>
            X
          </div>
        </InputGroupText>
        <Input type='number' value={getValue(x)} onChange={e => handleChange('x', e.target.value)} />
      </InputGroup>
      <InputGroup className='w-full'>
        <InputGroupText>
          <div className='w-[23px] text-center'>
            Y
          </div>
        </InputGroupText>
        <Input type='number' value={getValue(y)} onChange={e => handleChange('y', e.target.value)} />
      </InputGroup>
    </div>
    <InputGroup className='w-full'>
      <InputGroupText className='w-10'>
        <div className='w-8 text-center'>
          Blur
        </div>
      </InputGroupText>
      <Input type='number' value={getValue(blur)} onChange={e => handleChange('blur', e.target.value)} />
    </InputGroup>
  </div>
}

const useConvertOldValue = ({ prefix, setUnit: setUnitState }) => {
  const { value, setValue } = useCraftProp(`${prefix}.value`)
  const { value: unit, setValue: setUnit } = useCraftProp(`${prefix}.unit`)
  const { value: x, setValue: setX } = useCraftProp(`${prefix}.x`)
  const { value: y, setValue: setY } = useCraftProp(`${prefix}.y`)
  const { value: blur, setValue: setBlur } = useCraftProp(`${prefix}.blur`)
  const isArbitrary = useMemo(() => value === 'arbitrary', [value])

  useEffect(() => {
    if (isArbitrary) {
      const unitToUse = unit || 'px'
      setValue({ x: [x, unitToUse].join('_'), y: [y, unitToUse].join('_'), blur: [blur, unitToUse].join('_') })
      setX(undefined)
      setY(undefined)
      setBlur(undefined)
      setUnit(undefined)
      setUnitState(unitToUse)
    }
  }, [unit, isArbitrary, setUnit, setX, setY, setBlur, x, y, blur, setValue, setUnitState])
}

export const shadowInputPerview = values => {
  if (typeof values?.value === 'string' && values?.value !== 'arbitrary') {
    return values?.value
  }

  const parsedValues = values.value === 'arbitrary' ? { ...values, unit: values.unit || 'px' } : { ...values?.value, unit: values?.x?.split('_')[1] || 'px' }
  const { x, y, blur, unit } = parsedValues
  return [x && `${x.split('_')[0]}${unit}`, y && `${y.split('_')[0]}${unit}`, blur && `blur:${blur.split('_')[0]}${unit}`].filter(Boolean).join(' ')
}
